import { ref, type Ref } from "vue";

export const ServiceSymbol = Symbol();

export const LocalStorageIdentifier = "oekoprofit_identity";
export const LocalStorageSelectedTeilnahme = "oekoprofit_selectedTeilnahme";

interface ITeilnahme {
  id: string;
  name: string;
  auszeichnung: boolean;
  teilnahmejahr: {
    id: string
    name: string
  };
}

export interface ITextbaustein {
  inhalt: string;
  key: string;
  name: string;
}

export interface ITextbausteinRepository {
  get(key: string): Promise<ITextbaustein>;
}

export class TextbausteinRepository implements ITextbausteinRepository {
  private _service: IService;

  constructor(service: IService) {
    this._service = service;
  }

  get(key: string): Promise<ITextbaustein> {
    return this._service.get<ITextbaustein>(`/staticdata/textbausteine?key=${key}`, false);
  }
}

export enum FeedbackStatus {
  Offen = 180620001,
  Erledigt = 180620000,
  AbgeschlossenDurchReviewer = 42 // Virtual!
}

export interface AuditEntry {
  feedbackstatus: number;
  notiz?: string;
  erledigt?: boolean;
}

export interface IAuditable {
  auditEntry?: AuditEntry;
}

export interface IBetriebsprozess {
  id?: string;
  name?: string;
  beschreibung?: string;
  technischeVerfahren?: string;
  notiz?: string;
}

export interface IRaum {
  anzahlBeschaeftigte?: number;
  bereichId?: string;
  bereichName?: string;
  gebaeudeId?: string;
  flaeche?: number;
  id?: string;
  name?: string;
  notiz?: string;
  planreferenz?: string;
  produktionskapazitaet?: string;
  zimmernummer?: string;
}

export interface IOptionSetValue {
  id: number;
  name: string;
}

export interface IRecordWithUnit {
  id: string;
  name: string;
  einheit: string; // EntityReference.
}

export enum Role {
  Antragsteller = 180620000,
  Spezialist = 180620001 // == Reviewer
}

export interface Identity {
  userName: string;
  name: string;
  token: string;
  id: string;
  role: Role;
  contact: string;
  standortName: string;
  standortId: string;
  lastTeilnahmeId: string;
  lastTeilnahmeName: string;
  recordListFiftyFifty: boolean;
}

export interface IService {
  identity: Ref<Identity | null>;
  autosave: boolean;
  loading: boolean;
  saving: boolean;
  selectedTeilnahmeId: string;
  selectedTeilnahmeName: string;
  selectedTeilnahmeApproved: boolean;

  textbausteine: ITextbausteinRepository;

  isLatestTeilnahme: boolean;

  baseUri: string;

  get<T>(url: string, usetoken?: boolean): Promise<T>;
  post<T>(url: string, payload: any): Promise<T>;
  delete(url: string): Promise<void>;
  patch<T>(url: string, payload: any): Promise<T>;
  put<T>(url: string, payload: any): Promise<T>;

  executeRaw(url: string, method: string, usetoken?: boolean, payload?: any): Promise<string>;

  login(username: string, password: string): Promise<boolean>;
  logout(): void;

  switchStandort(standortId: string): Promise<void>;

  setTeilnahme(id: string): Promise<void>;
  approveTeilnahme(id: string): Promise<void>;
}

export class Service implements IService {
  private _identity: Ref<Identity | null>;
  private _autosave: Ref<boolean>;
  private _saving: Ref<boolean>;
  private _loading: Ref<boolean>;

  private _selectedTeilnahmeId: Ref<string>;
  private _selectedTeilnahmeName: Ref<string>;
  private _selectedTeilnahmeApproved: Ref<boolean>;

  public baseUri: string;

  constructor(baseUri: string) {
    this._identity = ref(null);
    this._autosave = ref(false);
    this._loading = ref(false);
    this._saving = ref(false);
    this._selectedTeilnahmeId = ref("");
    this._selectedTeilnahmeName = ref("");
    this._selectedTeilnahmeApproved = ref(false);

    this.baseUri = baseUri;
  }

  logout(): void {
    this._identity.value = null;
    localStorage.clear();
  }

  async login(username: string, password: string): Promise<boolean> {
    try {
      this._identity.value = await this.post<Identity>('/auth', { username: username, password: password });
      this.setTeilnahme(this._identity.value.lastTeilnahmeId);

      localStorage.setItem(LocalStorageIdentifier, JSON.stringify(this._identity.value));
      return true;
    }
    catch {
      return false;
    }
  }

  async switchStandort(standortId: string): Promise<void> {
    this._identity.value = await this.get<Identity>(`/auth/switch/${standortId}`, true);
    this.setTeilnahme(this._identity.value.lastTeilnahmeId);
    localStorage.setItem(LocalStorageIdentifier, JSON.stringify(this._identity.value));
  }

  async approveTeilnahme(id: string): Promise<void> {
    return await this.patch<any>("/teilnahmen/freigeben", { id: id, auszeichnung: true });
  }
  
  get identity(): Ref<Identity | null> {
    if (this._identity.value == null && localStorage.getItem(LocalStorageIdentifier) !== null)
      this._identity.value = JSON.parse(localStorage.getItem(LocalStorageIdentifier));

    return this._identity;
  }

  get autosave(): boolean {
    return this._autosave.value
  }

  set autosave(enable: boolean) {
    this._autosave.value = enable;
  }

  get loading(): boolean {
    return this._loading.value
  }

  set loading(enable: boolean) {
    this._loading.value = enable;
  }

  get saving(): boolean {
    return this._saving.value
  }

  set saving(enable: boolean) {
    this._saving.value = enable;
  }

  get textbausteine(): ITextbausteinRepository {
    return new TextbausteinRepository(this);
  }

  get selectedTeilnahmeId(): string {
    if (!this._selectedTeilnahmeId.value && localStorage.getItem(LocalStorageSelectedTeilnahme) !== null) {
      let selectedTeilnahme: ITeilnahme = JSON.parse(localStorage.getItem(LocalStorageSelectedTeilnahme));
      this._selectedTeilnahmeId.value = selectedTeilnahme.id;
    }

    return this._selectedTeilnahmeId.value;
  }

  get selectedTeilnahmeName(): string {
    if (!this._selectedTeilnahmeName.value && localStorage.getItem(LocalStorageSelectedTeilnahme) !== null) {
      let selectedTeilnahme: ITeilnahme = JSON.parse(localStorage.getItem(LocalStorageSelectedTeilnahme));
      this._selectedTeilnahmeName.value = selectedTeilnahme.teilnahmejahr.name;
    }

    return this._selectedTeilnahmeName.value;
  }

  get selectedTeilnahmeApproved(): boolean {
    if (!this._selectedTeilnahmeName.value && localStorage.getItem(LocalStorageSelectedTeilnahme) !== null) {
      let selectedTeilnahme: ITeilnahme = JSON.parse(localStorage.getItem(LocalStorageSelectedTeilnahme));
      this._selectedTeilnahmeApproved.value = selectedTeilnahme.auszeichnung;
    }

    return this._selectedTeilnahmeApproved.value;
  }

  get isLatestTeilnahme(): boolean {
    return this.selectedTeilnahmeId === this._identity.value?.lastTeilnahmeId;
  }

  async setTeilnahme(selectedId: string): Promise<void> {
    this._selectedTeilnahmeId.value = selectedId;

    let teilnahme: ITeilnahme = await this.get(`/teilnahmen/${selectedId}`, true);
    this._selectedTeilnahmeName.value = teilnahme.teilnahmejahr.name;
    this._selectedTeilnahmeApproved.value = teilnahme.auszeichnung

    localStorage.setItem(LocalStorageSelectedTeilnahme, JSON.stringify(teilnahme));
  }

  async execute<T>(url: string, method: string, usetoken?: boolean, payload?: any): Promise<T> {
    const headers = new Headers();
    headers.append("Accept", "application/json");

    if (usetoken && this.identity.value?.token) {
      headers.append("Authorization", `Bearer ${this.identity.value?.token}`);
    }

    if (payload) headers.append("Content-Type", "application/json");

    if (localStorage.getItem("impersonate") != null) {
      headers.append("act-on-behalf", localStorage.getItem("impersonate"));
    }

    let wasLoading = this.loading;
    let wasSaving = this.saving;

    if (method === "GET") {
      this.loading = true;
    }
    else {
      this.saving = true;
    }

    const response = await fetch(`${this.baseUri}${url}`, {
      method: method,
      cache: "no-cache",
      headers: headers,
      body: payload ? JSON.stringify(payload) : null,
    });

    if (method === "GET" && !wasLoading) {
      this.loading = false;
    }
    else if (!wasSaving) {
      this.saving = false;
    }

    if (!response.ok) {
      if (response.status == 401) {
        this.logout();
        location.reload();
      }
    }

    const text = await response.text();
    return text.length ? (JSON.parse(text) as Promise<T>) : null;
  }

  async executeRaw(
    url: string,
    method: string,
    usetoken?: boolean,
    payload?: any
  ): Promise<string> {
    const headers = new Headers();
    headers.append("Accept", "application/json");

    if (usetoken && this.identity.value?.token) {
      headers.append("Authorization", `Bearer ${this.identity.value?.token}`);
    }

    if (payload) headers.append("Content-Type", "application/json");

    if (localStorage.getItem("impersonate") != null) {
      headers.append("act-on-behalf", localStorage.getItem("impersonate"));
    }

    const response = await fetch(`${this.baseUri}${url}`, {
      method: method,
      cache: "no-cache",
      headers: headers,
      body: payload ? JSON.stringify(payload) : null,
    });

    if (!response.ok) {
      if (response.status == 401) {
        this.logout();
        location.reload();
      }

      throw new Error(response.statusText);
    }

    const text = await response.text();
    return text;
  }

  async get<T>(url: string, usetoken?: boolean): Promise<T> {
    return this.execute(url, "GET", usetoken);
  }

  async post<T = void>(url: string, payload: any): Promise<T> {
    return this.execute(url, "POST", true, payload);
  }

  async delete(url: string): Promise<void> {
    return this.execute(url, "DELETE", true);
  }

  async patch<T>(url: string, payload: any): Promise<T> {
    return this.execute(url, "PATCH", true, payload);
  }

  async put<T>(url: string, payload: any): Promise<T> {
    return this.execute(url, "PUT", true, payload);
  }
}
